// BKSA — týdenní Buy / Keep / Sell / Avoid predikce (Fáze 2).
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { TrendUp, Lock, ShieldCheck, TrendDown, Prohibit } from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import { useAuth } from "../contexts/AuthContext";
import { searchPlayers } from "../lib/lab-api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATS = [
    { key: "buy", label: "BUY", icon: TrendUp, color: "text-fa-mint border-fa-mint/50", hint: "Koho bych teď koupil" },
    { key: "keep", label: "KEEP", icon: ShieldCheck, color: "text-fa-sky border-fa-sky/50", hint: "Koho držet i přes pochybnosti" },
    { key: "sell", label: "SELL", icon: TrendDown, color: "text-fa-yellow border-fa-yellow/50", hint: "Koho prodat" },
    { key: "avoid", label: "AVOID", icon: Prohibit, color: "text-red-400 border-red-400/50", hint: "Komu se vyhnout" },
];

function CatCard({ cat, entry, locked, onSaved }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [player, setPlayer] = useState(entry ? { fpl_id: entry.fpl_id, name: entry.player_name, team: entry.team, position: entry.position } : null);
    const [horizon, setHorizon] = useState(entry?.horizon || 3);
    const [confidence, setConfidence] = useState(entry?.confidence ?? 50);
    const [reason, setReason] = useState(entry?.reason || "");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (q.trim().length < 2) { setResults([]); return; }
        const h = setTimeout(() => searchPlayers(q.trim(), 5).then((d) => setResults(d.results || [])).catch(() => {}), 250);
        return () => clearTimeout(h);
    }, [q]);

    const save = async () => {
        if (!player) { toast.error("Vyber hráče"); return; }
        setBusy(true);
        try {
            await axios.put(`${API}/bksa/entry`, {
                category: cat.key, fpl_id: player.fpl_id, horizon: Number(horizon),
                confidence: Number(confidence), reason,
            });
            toast.success(`${cat.label}: ${player.name} uložen`);
            onSaved();
        } catch (e) { toast.error(e.response?.data?.detail || "Uložení selhalo"); }
        finally { setBusy(false); }
    };

    return (
        <div className={`border bg-black/40 p-4 space-y-3 ${cat.color.split(" ")[1]}`} data-testid={`bksa-card-${cat.key}`}>
            <div className={`flex items-center gap-2 font-display font-black uppercase text-base ${cat.color.split(" ")[0]}`}>
                <cat.icon size={18} weight="bold" /> {cat.label}
                {locked && <Lock size={13} className="text-white/40 ml-auto" />}
            </div>
            <p className="font-mono text-[9px] text-white/45 -mt-2">{cat.hint}</p>
            {player ? (
                <div className="flex items-center justify-between border border-white/15 bg-black/60 px-3 py-2" data-testid={`bksa-player-${cat.key}`}>
                    <div>
                        <Link to={`/players/${player.fpl_id}`} data-testid={`bksa-profile-${cat.key}`}
                              className="font-display font-bold text-sm hover:text-fa-mint transition-colors">{player.name}</Link>
                        <div className="font-mono text-[9px] text-white/45 uppercase">{player.team} · {player.position}</div>
                    </div>
                    {!locked && (
                        <button type="button" onClick={() => setPlayer(null)} className="font-mono text-[9px] uppercase text-white/40 hover:text-white">Změnit</button>
                    )}
                </div>
            ) : !locked && (
                <div className="relative">
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat hráče…"
                           data-testid={`bksa-search-${cat.key}`}
                           className="w-full bg-black/40 border border-white/15 px-2.5 py-2 font-mono text-[11px] focus:outline-none focus:border-fa-mint placeholder:text-white/25" />
                    {results.length > 0 && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-[#0b0b0b] border border-fa-violet/40 z-30">
                            {results.map((p) => (
                                <button key={p.fpl_id} type="button"
                                        onClick={() => { setPlayer(p); setQ(""); setResults([]); }}
                                        data-testid={`bksa-pick-${cat.key}-${p.fpl_id}`}
                                        className="w-full text-left px-2.5 py-1.5 flex justify-between border-b border-white/5 hover:bg-fa-violet/20 font-mono text-[10px]">
                                    <span className="font-display font-bold text-xs">{p.name}</span>
                                    <span className="text-white/40">{p.team} · £{p.price}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
            {!locked && (
                <>
                    <div className="grid grid-cols-2 gap-2">
                        <label className="font-mono text-[8px] uppercase tracking-widest text-white/40 space-y-0.5 block">
                            <div>Horizont</div>
                            <select value={horizon} onChange={(e) => setHorizon(e.target.value)} data-testid={`bksa-horizon-${cat.key}`}
                                    className="w-full bg-black/40 border border-white/15 px-2 py-1.5 font-mono text-[10px] focus:outline-none">
                                <option value={1}>1 GW</option><option value={3}>3 GW</option><option value={8}>8 GW</option>
                            </select>
                        </label>
                        <label className="font-mono text-[8px] uppercase tracking-widest text-white/40 space-y-0.5 block">
                            <div>Confidence: {confidence}%</div>
                            <input type="range" min={0} max={100} value={confidence} onChange={(e) => setConfidence(e.target.value)}
                                   data-testid={`bksa-confidence-${cat.key}`} className="w-full accent-[#9EE8B4]" />
                        </label>
                    </div>
                    <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Krátké zdůvodnění (volitelné)"
                           data-testid={`bksa-reason-${cat.key}`}
                           className="w-full bg-black/40 border border-white/15 px-2.5 py-1.5 font-mono text-[10px] focus:outline-none focus:border-fa-mint placeholder:text-white/25" />
                    <button type="button" onClick={save} disabled={busy || !player} data-testid={`bksa-save-${cat.key}`}
                            className="w-full font-mono text-[10px] uppercase tracking-widest px-3 py-2 bg-fa-mint text-black font-bold disabled:opacity-40">
                        {busy ? "Ukládám…" : entry ? "Upravit predikci" : "Uložit predikci"}
                    </button>
                </>
            )}
            {locked && entry && (
                <div className="font-mono text-[10px] text-white/55 space-y-1">
                    <div>Horizont: {entry.horizon} GW · Confidence: {entry.confidence}%</div>
                    {entry.reason && <div className="text-white/40">„{entry.reason}"</div>}
                    <div className="text-fa-mint">Snapshot: £{entry.snapshot?.price} · {entry.snapshot?.ownership_pct}% own.</div>
                </div>
            )}
        </div>
    );
}

export default function Bksa() {
    const { user, login } = useAuth();
    const [data, setData] = useState(null);

    const load = useCallback(() => {
        axios.get(`${API}/bksa/current`).then((r) => setData(r.data)).catch(() => setData({ open: false }));
    }, []);
    useEffect(() => { load(); }, [load]);

    const filled = data?.entries ? Object.keys(data.entries).length : 0;

    return (
        <AppShell>
            <div className="max-w-5xl mx-auto space-y-6" data-testid="bksa-page">
                <header>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-mint">Týdenní predikce</div>
                    <h1 className="font-display font-black uppercase tracking-tight text-2xl md:text-3xl mt-1">
                        Buy · Keep · Sell · Avoid
                    </h1>
                    <p className="font-mono text-[10px] text-white/50 mt-2 max-w-2xl">
                        Před každým deadlinem vyplň 4 predikce. Po deadlinu se uzamknou se snapshotem dat a vyhodnotí
                        se po 1 / 3 / 8 GW. Aktivita = Knowledge XP, přesnost = Manager Rating.
                    </p>
                </header>

                {data === null && <p className="font-mono text-[11px] text-white/40">Načítám…</p>}
                {data && !data.open && (
                    <div className="border border-white/10 bg-black/40 p-6 font-mono text-[11px] text-white/55" data-testid="bksa-closed">
                        {data.reason || "Hra se otevře, jakmile bude vypsaný deadline dalšího GW."}
                    </div>
                )}
                {data?.open && (
                    <>
                        <div className="flex flex-wrap items-center gap-3 font-mono text-[10px] uppercase tracking-widest" data-testid="bksa-status">
                            <span className="px-3 py-1.5 border border-fa-mint/40 text-fa-mint">GW {data.gameweek}</span>
                            <span className="text-white/50">Deadline: {new Date(data.deadline_time).toLocaleString("cs")}</span>
                            {data.locked
                                ? <span className="px-3 py-1.5 border border-red-400/50 text-red-300 flex items-center gap-1.5"><Lock size={11} /> Uzamčeno</span>
                                : <span className="text-white/50">Vyplněno {filled}/4</span>}
                            {user && <span className="ml-auto text-fa-sky">Knowledge XP: {data.knowledge_xp ?? 0}</span>}
                        </div>
                        {!user ? (
                            <div className="border border-white/10 bg-black/40 p-6 text-center space-y-3" data-testid="bksa-login-cta">
                                <p className="font-mono text-[11px] text-white/55">Pro predikce se přihlas — ukládají se k tvému účtu.</p>
                                <button type="button" onClick={login} className="font-mono text-xs uppercase tracking-widest px-5 py-2.5 bg-fa-mint text-black font-bold">
                                    Přihlásit se
                                </button>
                            </div>
                        ) : (
                            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                {CATS.map((c) => (
                                    <CatCard key={`${c.key}-${data.entries?.[c.key]?.updated_at || "new"}`}
                                             cat={c} entry={data.entries?.[c.key]} locked={data.locked} onSaved={load} />
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </AppShell>
    );
}
